/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **A row an operator authors inside one artifact** (`#1580`, the shared-component sweep): its head, a
 * one-line summary, its marks, Edit (Apply and Cancel while open), its own actions, and the body it
 * opens into. Rows sit in an {@link AuthoredRowList}, separated by hairlines.
 *
 * It is what `MappingRow` was built on, taken out from under it. A round is a row of this kind and is
 * not a mapping: nothing is mapped, and the value chip, provenance, template revert and *Stop
 * overriding* would all be `null` on it. So the mapping parts stay on `MappingRow`, which composes
 * this, and a round composes it too (*is this a property of mappings, or of authored rows?*).
 *
 * What the shell owns, so no row kind can drop it:
 * - **the leading severity dot** (`#1412`): scanned down the list, so it leads and reserves its width;
 * - **opening**: a click on a closed row is its Edit, except a click on a control inside it;
 * - **Apply and Cancel** in the place Edit was (`#1262`'s Cancel semantics are the caller's snapshot);
 * - **the marks hold and the summary yields** under width pressure;
 * - **a read-only row opens read-only** (`LP3`, `#1588`), where the row has a read body to show.
 */

import React from 'react';
import { Box, Button, HStack } from '@chakra-ui/react';
import { RowEditActions } from './RowEditActions';
import type { MappingIssueLevel, RowSeverity } from './mapping-issues';

/** The bordered container rows sit in: hairline dividers, no card per row. */
export const AuthoredRowList: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box borderWidth="1px" borderColor="border.default" borderRadius="8px" overflow="hidden">
    {children}
  </Box>
);

export interface AuthoredRowProps {
  /** The left column: the row's name (and, for a mapping, its input while open). */
  head: React.ReactNode;
  /** The middle: what the row holds, on one line. It truncates; the marks do not. */
  summary: React.ReactNode;
  /** Marks between the summary and the actions. They never shrink. `null` for none. */
  marks: React.ReactNode | null;
  /** The leading dot. **Required and nullable** (`#1412`): `null` is a row with no finding. */
  severity: RowSeverity | null;
  editing: boolean;
  onEdit: () => void;
  onApply: () => void;
  onCancel: () => void;
  editLabel: string;
  /** The row's own actions after Edit: a mapping's ✕, a round's menu. `null` for none. */
  actions: React.ReactNode | null;
  /** Preview: head, summary and marks, with no edit actions and a tighter rhythm. */
  readOnly: boolean;
  /**
   * **What a read-only row opens to** (`LP3`, `#1588`): the item read-only, with editing left to the
   * section. **Required and nullable**: `null` is a row that does not open when read-only, and saying so
   * is the row's decision, not a default it inherits.
   */
  readView: React.ReactNode | null;
  /**
   * **Told when a read-only row opens or closes** (`#1603`), so the list can carry the open row into
   * its editor. **Required and nullable**: `null` is a list that does not follow it.
   */
  onReadingChange: ((open: boolean) => void) | null;
  /** Start open in the read view, for a row the list had open before it remounted. Defaults to closed. */
  initiallyReading?: boolean;
  /** `flow` drops the hairline and rounds the hover: the guided tree's treatment (#989). */
  variant?: 'list' | 'flow';
  /** Highlight the row (added or updated by a bulk change). */
  highlighted?: boolean;
  /** The outermost element, for `useRevealOnOpen`. */
  rootRef?: React.Ref<HTMLDivElement>;
  /** The row's DOM id, so it can be revealed without being opened (`#1305`). */
  rootId?: string;
  /** The body, rendered only while open. */
  children?: React.ReactNode;
}

export const AuthoredRow: React.FC<AuthoredRowProps> = ({
  head,
  summary,
  marks,
  severity,
  editing,
  onEdit,
  onApply,
  onCancel,
  editLabel,
  actions,
  readOnly,
  readView,
  onReadingChange,
  initiallyReading = false,
  variant = 'list',
  highlighted,
  rootRef,
  rootId,
  children,
}) => {
  const active = !readOnly && (editing || highlighted);
  const [reading, setReadingState] = React.useState(initiallyReading);
  const setReading = (open: boolean) => {
    setReadingState(open);
    onReadingChange?.(open);
  };
  const opensRead = readOnly && readView !== null;
  const bodyId = React.useId();
  return (
    <Box
      ref={rootRef}
      id={rootId}
      borderTopWidth={variant === 'flow' ? undefined : '1px'}
      // `border.default`, not `subtle`: in light mode `subtle` (#EDEEF3) is within a shade of the card
      // and of the row's own hover surface, so the line between rows disappeared (`#1588`).
      borderColor={variant === 'flow' ? undefined : 'border.default'}
      _first={variant === 'flow' ? undefined : { borderTopWidth: 0 }}
      borderRadius={variant === 'flow' ? 'md' : undefined}
      bg={active ? 'accent.subtle' : 'transparent'}
      _hover={(readOnly && !opensRead) || editing ? undefined : { bg: active ? 'accent.subtle' : 'bg.subtle' }}
    >
      <HStack
        gap={3}
        align="center"
        px={2}
        py={readOnly ? 1 : 2}
        // **Clicking a closed row opens it**, the same as its Edit (Todd, 2026-09-28). A click that lands on
        // a control inside the row — a mark's revert, the ✕, Edit itself — is that control's, not the row's.
        // Keyboard users keep Edit, which is where focus already goes; the row is not a second tab stop.
        cursor={(readOnly && !opensRead) || editing ? undefined : 'pointer'}
        onClick={
          (readOnly && !opensRead) || editing
            ? undefined
            : (e) => {
                const target = e.target as HTMLElement;
                if (target.closest('button, a, input, select, textarea, [role="button"], [role="menuitem"]')) return;
                if (opensRead) setReading(!reading);
                else onEdit();
              }
        }
      >
        {/* Severity leads: it is scanned vertically down a list of rows, so a dot
            beside the actions is found only after reading the row it should triage.
            Reserved width keeps names aligned whether or not a row has one. */}
        <Box w="8px" flexShrink={0} display="flex" justifyContent="center">
          {severity ? <SeverityDot severity={severity} /> : null}
        </Box>
        <Box minW="150px" maxW="240px" flexShrink={0}>
          {head}
        </Box>
        <Box flex="1" minW="120px" overflow="hidden">
          {summary}
        </Box>
        {marks}
        {opensRead && (
          <Button
            size="xs"
            variant="ghost"
            color="accent.solid"
            flexShrink={0}
            aria-expanded={reading}
            aria-controls={bodyId}
            onClick={() => setReading(!reading)}
          >
            {reading ? 'Hide' : 'Show'}
          </Button>
        )}
        {!readOnly && (
          <HStack gap={1} flexShrink={0}>
            {editing ? (
              <RowEditActions onApply={onApply} onCancel={onCancel} />
            ) : (
              <Button size="xs" variant="ghost" color="accent.solid" onClick={onEdit} aria-label={editLabel}>
                Edit
              </Button>
            )}
            {actions}
          </HStack>
        )}
      </HStack>
      {!readOnly && editing && (
        <Box px={3} pb={3} pt={1}>
          {children}
        </Box>
      )}
      {opensRead && reading && (
        <Box id={bodyId} px={3} pb={3} pt={1}>
          {readView}
        </Box>
      )}
    </Box>
  );
};

/**
 * Row-scale severity marker, reusing the section-level taxonomy (§16) rather than
 * inventing a second one. Never dropped under width pressure: it is the only mark
 * that says something is wrong, and a row hiding its warning to fit a chip has its
 * priorities inverted.
 */
export const SEVERITY_TONE: Record<MappingIssueLevel, string> = {
  error: 'error.solid',
  warn: 'warning.solid',
  info: 'fg.subtle',
};

const SeverityDot: React.FC<{ severity: RowSeverity }> = ({ severity }) => (
  <Box
    w="6px"
    h="6px"
    borderRadius="full"
    bg={SEVERITY_TONE[severity.level]}
    // A structural handle for the per-surface tests: what they assert is *which row carries the
    // mark*, and matching on the label would tie them to its wording.
    data-severity={severity.level}
    // **The accessible name is the finding, not the colour.** `${level} on this row` named our own
    // severity vocabulary and described nothing, so the one mark that says something is wrong said
    // only that. The `title` and the label carry the same sentences, so the hover adds convenience
    // rather than being the only way to read it (`#1276`).
    aria-label={`${severity.level}: ${severity.detail}`}
    title={severity.detail}
    role="img"
  />
);
